import { RSAA } from 'redux-api-middleware';
import { getAccountsPage, getPayeesPage } from '../../helpers/apiRequestBuilder';
import { ACCOUNT_CAPABILITIES, TYPE_FASTER_PAYMENTS, TYPE_SO, TYPE_FASTER_PAYMENTS_TO } from '../../config/';

import {
	SET_ACCOUNTS_SEARCH_TEXT,
	GET_ACCOUNTS_PAGE_SUCCESS,
	GET_ACCOUNTS_PAGE_REQUEST,
	GET_ACCOUNTS_PAGE_FAILURE,
	GET_PAYEES_PAGE_REQUEST,
	GET_PAYEES_PAGE_SUCCESS,
	GET_PAYEES_PAGE_FAILURE,
	SAVE_LAST_API_ACTION,
	SET_SHOW_PAYEES,
	SET_PAGING_LAYOUT_TYPE,
	SET_PAYEES_SEARCH_TEXT,
	SET_ACCOUNTS_PAGE_SIZE,
	SET_PAYEES_PAGE_SIZE,
	SET_ACCOUNTS_CURRENT_PAGE,
	SET_PAYEES_CURRENT_PAGE,
	RESET_PAGED_PAYEES,
	GET_ACCOUNTS_BY_CAPABILITY_REQUEST,
	GET_ACCOUNTS_BY_CAPABILITY_SUCCESS,
	GET_ACCOUNTS_BY_CAPABILITY_FAILURE,
	SET_IS_SELECTING_LCV_FROM_ACCOUNT,
} from './constants';
import {
	getCurrentFilterCriteria,
	getLastApiAction,
	getBenType,
	isAccountsPagingLayout,
	isSelectingLCVFromAccount,
} from './selectors';
import { getPaymentType, getIsLCV } from '../modes/selectors';
import { isRepeatingPayment } from '../payments/selectors';
import endpoints from '../../utils/endpoints';

export const saveLastApiAction = (lastFailedApiAction) => ({
	type: SAVE_LAST_API_ACTION,
	lastFailedApiAction,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const getAccountsByCapability = (capability) => {
	const { x_bpi_version, method } = endpoints.getLcvAccounts;
	const accountCapability = ACCOUNT_CAPABILITIES[capability] || '';
	const endpoint = endpoints.getLcvAccounts.resolve({ paymentType: encodeURIComponent(accountCapability) });
	return {
		[RSAA]: {
			endpoint,
			method,
			types: [
				GET_ACCOUNTS_BY_CAPABILITY_REQUEST,
				GET_ACCOUNTS_BY_CAPABILITY_SUCCESS,
				GET_ACCOUNTS_BY_CAPABILITY_FAILURE,
			],
			headers: { 'x-bpi-version': x_bpi_version },
		},
	};
};

export const getPageAccounts = (pageToFetch, state) => {
	const { pageSize, searchText } = getCurrentFilterCriteria(state);
	const paymentType = getPaymentType(state);
	const isRepeatingPaymentFlag = isRepeatingPayment(state);
	const selectingLCVFromAccount = isSelectingLCVFromAccount(state);
	// FIXME: Issues with SO as we dont know the type when they pick the acc so this wont be possible to discuss with SD, leaving logic in here for now
	let capabilityMapping = ACCOUNT_CAPABILITIES[isRepeatingPaymentFlag ? TYPE_SO : paymentType] || '';
	if (paymentType === TYPE_FASTER_PAYMENTS && !selectingLCVFromAccount) {
		capabilityMapping += `,${ACCOUNT_CAPABILITIES[TYPE_FASTER_PAYMENTS_TO]}`;
	}

	const isLCV = getIsLCV(state);
	const { method, uri, x_bpi_version } = getAccountsPage({
		search: searchText,
		size: pageSize,
		page: pageToFetch,
		paymentType: encodeURIComponent(capabilityMapping),
	});
	return {
		[RSAA]: {
			endpoint: uri,
			method,
			types: [
				GET_ACCOUNTS_PAGE_REQUEST,
				{
					type: GET_ACCOUNTS_PAGE_SUCCESS,
					meta: {
						isLCV,
					},
				},
				GET_ACCOUNTS_PAGE_FAILURE,
			],
			headers: { 'x-bpi-version': x_bpi_version },
		},
	};
};

export const getPagePayees = (pageToFetch, state) => {
	const { pageSize, searchText } = getCurrentFilterCriteria(state);
	const benType = getBenType(state);
	const { method, uri, x_bpi_version } = getPayeesPage({
		search: searchText,
		size: pageSize,
		page: pageToFetch,
		benType,
	});
	return {
		[RSAA]: {
			endpoint: uri,
			method,
			types: [
				GET_PAYEES_PAGE_REQUEST,
				{
					type: GET_PAYEES_PAGE_SUCCESS,
					meta: { searchText },
				},
				GET_PAYEES_PAGE_FAILURE,
			],
			headers: { 'x-bpi-version': x_bpi_version },
		},
	};
};

export const getPage = (pageToFetch) => (dispatch, getState) => {
	const state = getState();
	const pageAction = isAccountsPagingLayout(state)
		? getPageAccounts(pageToFetch, state)
		: getPagePayees(pageToFetch, state);
	dispatch(saveLastApiAction(pageAction));
	dispatch(pageAction);
};

export const setSearchText = (searchText) => (dispatch, getState) => {
	const state = getState();
	if (isAccountsPagingLayout(state)) {
		return dispatch({
			type: SET_ACCOUNTS_SEARCH_TEXT,
			searchText,
			meta: {
				allowedDuringApiCall: true,
			},
		});
	}
	return dispatch({
		type: SET_PAYEES_SEARCH_TEXT,
		searchText,
		meta: {
			allowedDuringApiCall: true,
		},
	});
};

export const setPageSize = (pageSize) => (dispatch, getState) => {
	const state = getState();
	if (isAccountsPagingLayout(state)) {
		return dispatch({
			type: SET_ACCOUNTS_PAGE_SIZE,
			pageSize,
		});
	}
	return dispatch({
		type: SET_PAYEES_PAGE_SIZE,
		pageSize,
	});
};

export const setCurrentPage = (page) => (dispatch, getState) => {
	const state = getState();
	if (isAccountsPagingLayout(state)) {
		return dispatch({
			type: SET_ACCOUNTS_CURRENT_PAGE,
			page,
			meta: {
				allowedDuringApiCall: true,
			},
		});
	}
	return dispatch({
		type: SET_PAYEES_CURRENT_PAGE,
		page,
		meta: {
			allowedDuringApiCall: true,
		},
	});
};

export const retryLastApiAction = () => (dispatch, getState) => {
	const state = getState();
	const lastApiAction = getLastApiAction(state);
	dispatch(lastApiAction);
};

export const setShowPayees = (showPayees) => ({
	type: SET_SHOW_PAYEES,
	showPayees,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const setPagingLayoutType = (layoutType) => ({
	type: SET_PAGING_LAYOUT_TYPE,
	layoutType,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const resetPagedPayees = () => ({
	type: RESET_PAGED_PAYEES,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const setIsSelectingLCVFromAccount = (isSelectingLCVFromAccount) => ({
	type: SET_IS_SELECTING_LCV_FROM_ACCOUNT,
	payload: isSelectingLCVFromAccount,
	meta: {
		allowedDuringApiCall: true,
	},
});
